import { ImSys } from '../core/ImSys.js';

const utils = {
    /** Утилита подгрузки CSS один раз */
    loadCss(href) {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`link[href="${href}"]`)) {
                resolve();
                return;
            }
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = href;
            link.onload = () => resolve();
            link.onerror = () => reject(new Error(`Не удалось загрузить CSS: ${href}`));
            document.head.appendChild(link);
        });
    },
    /**
     * Форматирует Date в строку по заданному формату.
     * Поддерживает токены: yyyy, yy, MM, M, dd, d
     *
     * @example
     * utils.formatDateByFormat(new Date(2025, 11, 11), "dd.MM.yyyy") → "11.12.2025"
     */
    formatDateByFormat(date, format) {
        const yyyy = date.getFullYear();
        const yy = yyyy % 100;
        const M = date.getMonth() + 1;
        const MM = String(M).padStart(2, "0");
        const d = date.getDate();
        const dd = String(d).padStart(2, "0");
        return format
            .replace(/yyyy/g, String(yyyy))
            .replace(/yy/g, String(yy).padStart(2, "0"))
            .replace(/MM/g, MM)
            .replace(/M/g, String(M))
            .replace(/dd/g, dd)
            .replace(/d/g, String(d));
    },
    /**
    * Парсит строку в Date по формату
    * Поддерживает: dd.MM.yyyy, yyyy-MM-dd, dd/MM/yyyy, d.M.yy и похожие (без времени)
    */
    parseDateByFormat(str, format) {
        str = str.trim();
        const tokens = ['yyyy', 'yy', 'MM', 'M', 'dd', 'd'];
        const parts = [];
        let fmt = format;
        while (fmt.length > 0) {
            let found = false;
            for (const token of tokens) {
                if (fmt.startsWith(token)) {
                    parts.push({ token });
                    fmt = fmt.slice(token.length);
                    found = true;
                    break;
                }
            }
            if (!found) {
                parts.push({ sep: fmt[0] });
                fmt = fmt.slice(1);
            }
        }
        let regexpStr = '';
        for (const part of parts) {
            if (part.token) {
                switch (part.token) {
                    case 'yyyy':
                        regexpStr += '(\\d{4})';
                        break;
                    case 'yy':
                        regexpStr += '(\\d{2})';
                        break;
                    case 'MM':
                    case 'dd':
                    case 'M':
                    case 'd':
                        regexpStr += '(\\d{1,2})';
                        break;
                }
            }
            else if (part.sep) {
                regexpStr += part.sep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            }
        }
        const match = new RegExp('^' + regexpStr + '$').exec(str);
        if (!match)
            return null;
        let year;
        let month;
        let day;
        let valueIdx = 1;
        for (const part of parts) {
            if (part.token) {
                const val = match[valueIdx++];
                switch (part.token) {
                    case 'yyyy':
                        year = parseInt(val, 10);
                        break;
                    case 'yy':
                        year = 2000 + parseInt(val, 10);
                        break;
                    case 'MM':
                    case 'M':
                        month = parseInt(val, 10) - 1;
                        break;
                    case 'dd':
                    case 'd':
                        day = parseInt(val, 10);
                        break;
                }
            }
        }
        if (typeof day === 'number' &&
            typeof month === 'number' &&
            typeof year === 'number') {
            const date = new Date(year, month, day);
            if (date.getFullYear() === year &&
                date.getMonth() === month &&
                date.getDate() === day) {
                return date;
            }
        }
        return null;
    },
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    },
    /**
     * Сравнивает два массива поэлементно.
     * Работает для примитивов, а также для вложенных массивов (рекурсивно).
     */
    arraysEqual(a, b) {
        if (a === b)
            return true;
        if (!a || !b)
            return false;
        if (a.length !== b.length)
            return false;
        for (let i = 0; i < a.length; i++) {
            const v1 = a[i];
            const v2 = b[i];
            if (Array.isArray(v1) && Array.isArray(v2)) {
                if (!this.arraysEqual(v1, v2))
                    return false;
            }
            else if (v1 !== v2) {
                return false;
            }
        }
        return true;
    },
    /**
     *
     * @param name Имя иконки
     * @param cssClass Класс
     * @param style Инлайновые стили
     * @param src Полный путь до спрайта /src/vs/svg/sprite/sprite.svg
     * @returns
     */
    createSvg(name, cssClass, style, src) {
        const siteAttr = ImSys.siteBaseKey;
        let libPath;
        if (siteAttr) {
            let rootNode = siteAttr.split(".")[0]; // "vs"
            libPath = rootNode;
        }
        else
            libPath = 'vs';
        src == undefined ? src = `/src/${libPath}/svg/sprite/sprite.svg` : src;
        cssClass == undefined ? cssClass = '' : cssClass;
        style == undefined ? style = '' : style;
        let css = `class='svg-icon ${cssClass}'`;
        let inlineStyles = '';
        if (style)
            inlineStyles = `style='${style}'`;
        return `<svg ${css} ${inlineStyles}><use href='${src}#${name}'></use></svg>`;
    },
    /**
     * Поменять ссылку на иконку у <use> внутри svg-спрайта.
     * Работает с внешним спрайтом вида "/path/sprite.svg#icon".
     *
     * @param root Элемент, внутри которого ищем <use> (обычно кнопка)
     * @param name Имя иконки в спрайте
     * @param src Полный путь до спрайта (если не задан, берём тот, что уже стоит)
     */
    setSvgIcon(root, name, src) {
        const use = root.querySelector("use");
        if (!use)
            return false;
        // Если src не передали — возьмём из текущего href до '#'
        let base = src;
        if (!base) {
            const current = use.getAttribute("href") || use.getAttribute("xlink:href") || "";
            const hash = current.indexOf("#");
            base = hash >= 0 ? current.slice(0, hash) : current;
        }
        const next = `${base}#${name}`;
        // современно
        use.setAttribute("href", next);
        return true;
    },
    // Пытаемся через современный API (требует secure context: https:)
    async copyToClipboard(text) {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                return true;
            }
        }
        catch {
            /* ignore — пойдём в fallback */
        }
        // Fallback через временное textarea + execCommand('copy')
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "true");
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        let ok = false;
        try {
            // eslint-disable-next-line deprecation/deprecation
            // @ts-ignore — execCommand помечен как deprecated, но нам нужен как запасной путь
            ok = document.execCommand("copy");
        }
        catch {
            console.error("Copy not supported or blocked. Press Ctrl+C to copy.");
        }
        finally {
            document.body.removeChild(ta);
        }
        return ok;
    }
};

export { utils };
